import DatepickerIcon from "@assets/images/date-picker.svg";
import { Button } from "@material-ui/core";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { makeStyles } from "@saleor/macaw-ui";
import classNames from "classnames";
import locale from "date-fns/locale/en-US";
import moment from "moment-timezone";
import React, { useState } from "react";
import SVG from "react-inlinesvg";

const useStyles = makeStyles(
  theme => ({
    buttonToday: {
      backgroundColor: "#EEE !important",
      width: "100px",
      fontSize: "14px",
      "& .MuiButton-label": {
        fontWeight: 400
      },
      "&:hover": {
        border: "0 !important",
        backgroundColor: "#E0E0E0 !important",
        color: `${theme.palette.grey[900]} !important`
      }
    },
    svgDatePicker: {
      marginRight: "0.8rem"
    },
    buttonApply: {
      borderRadius: "4px",
      padding: "0.4rem 0.8rem",
      fontWeight: 400,
      fontSize: "1.2rem",
      lineHeight: "18px",
      background: "#009FB8",
      color: "white",
      marginLeft: "2px",
      boxShadow:
        "rgba(0, 0, 0, 0.16) 0px 2px 3px, rgba(0, 0, 0, 0.23) 0px 2px 3px",
      "&:hover, &:active": {
        color: "#009FB8",
        background: "#F2FAFB",
        outline: "1px solid #009FB8",
        boxShadow:
          "rgba(0, 0, 0, 0.16) 0px 2px 3px, rgba(0, 0, 0, 0.23) 0px 2px 3px"
      },
      "& path": {
        stroke: "white"
      },
      "&:hover path, &:active path": {
        stroke: "#009FB8"
      }
    },
    btnApplyOpen: {
      color: "#009FB8",
      background: "#F2FAFB",
      outline: "1px solid #009FB8",
      boxShadow:
        "rgba(0, 0, 0, 0.16) 0px 2px 3px, rgba(0, 0, 0, 0.23) 0px 2px 3px",
      "& path": {
        stroke: "#009FB8"
      }
    },
    datePickerPopper: {
      "& .MuiPickersDay-root": {
        fontSize: "1.6rem",
        borderRadius: "10px"
      },
      "& .MuiPickersDay-root:focus": {
        backgroundColor: "#FFFFFF"
      },
      "& .MuiPickersDay-root.Mui-selected, .MuiPickersDay-root.Mui-selected:hover, & .MuiPickersDay-root.Mui-selected:focus, .MuiPickersYear-yearButton.Mui-selected, .MuiPickersYear-yearButton.Mui-selected:hover, .MuiPickersYear-yearButton.Mui-selected:focus, .MuiPickersMonth-monthButton.Mui-selected, .MuiPickersMonth-monthButton.Mui-selected:hover, & .MuiPickersMonth-monthButton.Mui-selected:focus": {
        backgroundColor: "#009FB8"
      },
      "& .MuiPickersDay-root.Mui-selected:hover, & .MuiPickersDay-root.Mui-selected:focus, .MuiPickersYear-yearButton.Mui-selected:hover, .MuiPickersMonth-monthButton.Mui-selected:hover": {
        transform: "scale(1.05)"
      },
      "& .MuiPickersDay-today:not(.Mui-selected)": {
        border: "1px solid #009FB8 !important"
      },
      "& .MuiPickersYear-yearButton, .MuiPickersMonth-monthButton": {
        fontSize: "1.5rem"
      },
      "& .MuiDayCalendar-weekDayLabel": {
        fontSize: "16px"
      },
      "& .MuiPickersCalendarHeader-label": {
        fontSize: "20px"
      },
      "& .MuiPickersCalendarHeader-switchViewIcon": {
        fontSize: "25px"
      },
      "& .MuiPickersArrowSwitcher-root": {
        "& .MuiIconButton-root": {
          fontSize: "2.5rem"
        }
      },
      "& .MuiPickersLayout-actionBar": {
        borderTop: " 1px solid #e0e0e0",
        marginRight: "25px",
        marginLeft: "25px",
        paddingTop: "15px",
        paddingBottom: "20px",
        display: "flex",
        justifyContent: "space-between"
      },
      "& .MuiDayCalendar-slideTransition": {
        minHeight: "240px"
      },
      "& .acceptBtn": {
        width: "100px",
        padding: "5px",
        fontSize: "14px"
      }
    },
    datePickerv2: {
      width: "100%",
      "& .MuiSvgIcon-root": {
        fontSize: "25px !important"
      },
      "& .MuiOutlinedInput-input": {
        padding: "5px"
      },
      "& .MuiInputBase-adornedEnd": {
        "& input": {
          pointerEvents: "none"
        }
      }
    }
  }),
  { name: "DatePickerApplyAll" }
);

interface DatePickerProps {
  onApplyAll: (value) => void;
  minDate?: string | number | Date | moment.Moment;
  maxDate?: string | number | Date | moment.Moment;
  disabled?: boolean;
  id?: string;
}

const DateFormat = "YYYY-MM-DD";

const formatValue = value => (value ? moment(value).format(DateFormat) : null);
if (locale && locale.options) {
  locale.options.weekStartsOn = 1;
}

const ButtonField = (props: any) => {
  const {
    setOpen,
    InputProps: { ref = null } = {},
    slotProps,
    disabled
  } = props;
  const classes = useStyles();

  return (
    <>
      <Button
        ref={ref}
        disabled={disabled}
        className={classNames(
          classes.buttonApply,
          slotProps.open && classes.btnApplyOpen
        )}
        variant="contained"
        onClick={() => setOpen?.(prev => !prev)}
        name="apply-all-request-button"
        id="apply-all-request-button"
      >
        <SVG src={DatepickerIcon} className={classes.svgDatePicker} />
        Apply all
      </Button>
    </>
  );
};

const CustomActionBar = (props: any) => {
  const { onAccept, onSetToday, actions, className } = props;
  const classes = useStyles();

  if (actions == null || actions.length === 0) {
    return null;
  }

  return (
    <div className={className}>
      {actions?.map(actionType => {
        switch (actionType) {
          case "accept":
            return (
              <Button
                id="applyAllDateButton"
                variant={"contained"}
                className={"defaultB acceptBtn"}
                onClick={() => {
                  onAccept();
                  props.setOpen(false);
                }}
              >
                APPLY ALL
              </Button>
            );
          case "today":
            return (
              <Button
                id="toDayButton"
                className={classes.buttonToday}
                onClick={onSetToday}
              >
                Today
              </Button>
            );
          default:
            return null;
        }
      })}
    </div>
  );
};

const ButtonDatePicker = (props: any) => {
  const [open, setOpen] = React.useState(false);
  const classes = useStyles();
  const minDate = props.minDate
    ? moment(props.minDate)
    : moment().subtract(10, "years");
  const maxDate = props.maxDate
    ? moment(props.maxDate)
    : moment().add(10, "years");

  const onSetToday = () => {
    props.onChange(moment());
  };

  return (
    <DesktopDatePicker
      slots={{
        field: ButtonField,
        ...{ ...props.slots, ...{ open } },
        actionBar: CustomActionBar
      }}
      slotProps={{
        field: { setOpen } as any,
        open,
        popper: {
          className: classes.datePickerPopper
        },
        actionBar: {
          actions: ["today", "accept"],
          onAccept: props.handleApplyAll,
          onSetToday,
          setOpen
        }
      }}
      {...props}
      open={open}
      onClose={() => setOpen(false)}
      onOpen={() => setOpen(true)}
      closeOnSelect={false}
      minDate={minDate}
      maxDate={maxDate}
    />
  );
};

const DatePickerComponent: React.FC<DatePickerProps> = ({
  onApplyAll,
  ...args
}) => {
  const [date, setDate] = useState<Date | null>(new Date());
  const handleChange = (date: Date) => {
    setDate(date);
  };

  const handleApplyAll = (customDate?: Date) => {
    onApplyAll(formatValue(customDate ? customDate : date));
  };

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <ButtonDatePicker
        label={"Apply all"}
        value={moment(date)}
        onChange={handleChange}
        handleApplyAll={handleApplyAll}
        {...args}
      />
    </LocalizationProvider>
  );
};

export default React.memo(DatePickerComponent);
